// Spatial audio: the current MediaItem's audio is mixed and played from every speaker position in the
// manifest through a PannerNode (HRTF on desktop, equalpower on mobile and Safari). The listener follows the
// camera; level and tone by the listener's position come from speakers.ts (the room's directivity). A generated
// room impulse response adds the room's sound (not on mobile; room-sound.ts).
//
// Playback clock: for media elements, the first element's currentTime; for the synth buffer, the
// AudioContext clock. Everything is seekable so the multiplayer music state can drive it.
//
// Items with several audio URLs (Voyage: primary + ambient) play them together; the first is the clock and
// ends the item, the others loop. Item changes cross-fade (old out over fade_out_s, new in over fade_in_s,
// -60 dB <-> 0 dB) with an optional brown-noise bridge, following Voyage's playback.js (see NOTES.md).

import type { Camera } from 'three';
import { Vector3 } from 'three';
import type { Manifest } from '../scene/manifest';
import { DEFAULT_TRANSITION, type MediaItem, type Transition } from './media';
import { makeBrownNoise } from './brown-noise';
import { OverlayNoise } from './overlay-noise';
import { RoomSound, type RoomAudio } from './room-sound';
import { Speakers, hold } from './speakers';
import { wrapDelta } from '../outdoor/field';

type Track =
  | { kind: 'element'; els: HTMLAudioElement[]; nodes: MediaElementAudioSourceNode[]; gain: GainNode }
  | { kind: 'buffer'; buffer: AudioBuffer; node: AudioBufferSourceNode | null; startedAt: number; offset: number; gain: GainNode };

const _pos = new Vector3();
const _fwd = new Vector3();
const _up = new Vector3();

export class AudioEngine {
  readonly ctx: AudioContext;
  private mix: GainNode;
  /** after the room's tone filter (if any): what the speakers and the reverb take */
  private bus: GainNode;
  /** low-pass and reverb between mix and bus (room-sound.ts) */
  readonly room: RoomSound;
  private master: GainNode;
  /** The current room's sound sources: bus -> filters -> per source gain -> panner -> master (speakers.ts). */
  readonly speakers: Speakers;
  private track: Track | null = null;
  /** Tracks of replaced items, fading out. */
  private fading = new Set<Track>();
  private noise: AudioBufferSourceNode | null = null;
  private noiseGain: GainNode;
  private loading: Promise<void> | null = null;
  private synthCache: Promise<AudioBuffer> | null = null;
  item: MediaItem | null = null;
  playing = false;
  /** Seeks done by drift correction (tests, HUD). */
  readonly counters = { seeks: 0, ambientResyncs: 0 };
  private lastListener = -Infinity;
  private lastPos: Vector3 | null = null;
  /** Outdoor field (manifest `outdoor`): its size, and the sources' centre (x, z); 0 = no wrapping. */
  private wrapP = 0;
  private wrapC: [number, number] = [0, 0];
  /** A listener update skipped by the rate limit, applied by flushListener(). */
  listenerPending: Camera | null = null;
  private ambientStrikes = 0;
  /**
   * Seek lead for media elements while playing: a seek stalls the element's clock while it buffers (WebKit:
   * ~0.33 s from R2), so it lands behind the target, and the drift check reseeks again: a stutter loop.
   * The lead is learned from the residual measured ~1.2 s after each seek.
   */
  seekLead = 0;
  private seekCheck: { target: number; at: number } | null = null;
  /** Called when the playing state or item changes (UI). */
  onChange: () => void = () => {};
  /** Called when the current item's first track reaches its end while playing. */
  onEnded: (item: MediaItem) => void = () => {};

  /**
   * `panning`: HRTF (convolution per speaker, best localisation on headphones) or equalpower (cheap; used on
   * mobile and Safari). `reverb`: the generated room response (a convolver; off on mobile).
   */
  constructor(manifest: Manifest, readonly opts: { panning: PanningModelType; reverb: boolean } = { panning: 'HRTF', reverb: true },
    roomAudio: RoomAudio = manifest.audio) {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx({ latencyHint: 'playback' });
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    // gentle limiter so two coherent speakers never clip
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -10;
    comp.knee.value = 8;
    comp.ratio.value = 4;
    comp.attack.value = 0.005;
    comp.release.value = 0.25;
    this.master.connect(comp).connect(this.ctx.destination);

    this.mix = this.ctx.createGain();
    this.bus = this.ctx.createGain();
    this.noiseGain = this.ctx.createGain();
    this.noiseGain.gain.value = 0;
    this.noiseGain.connect(this.mix);
    const sources = sourcesOf(manifest.audio.sources);
    this.setWrap(manifest.outdoor?.size_m ?? 0, sources);
    this.speakers = new Speakers(this.ctx, this.bus, this.master, opts.panning, sources, !opts.reverb);
    this.speakers.setDirectivity(roomAudio.directivity);
    this.room = new RoomSound(this.ctx, this.mix, this.bus, this.master, opts.reverb, { ...roomAudio, sources });
  }

  /**
   * The room's sound (the manifest's audio with rooms.json audio over it; 2026-10-28, the concrete room): audio.lowpass_hz
   * (a gentle low-pass on the music before the speakers and the reverb), audio.reverb_send (the default reverb's level),
   * audio.reverb (a gated reverb generated for the room, shared/audio.ts); 2026-10-29: audio.directivity (how the
   * sources radiate, speakers.ts) and audio.modulation (a slow wobble on the low-pass path, room-sound.ts). Missing =
   * no filter, the default reverb and directivity (every room before). Called on every room switch; every change
   * ramps.
   */
  setRoomSound(a: RoomAudio = {}): void {
    this.speakers.setDirectivity(a.directivity);
    this.room.set({ ...a, sources: a.sources?.length ? a.sources : sourcesOf([]) });
  }

  /** The Basement overlay's brown noise (overlay-noise.ts), made on first use. */
  overlayNoise: OverlayNoise | null = null;

  /**
   * The overlay's brown noise level (2026-10-30, docs/plans/basement-overlay.md): glides toward `level` with the time
   * constant `tau` (s); 0 fades it out and then stops the source.
   */
  setOverlayNoise(level: number, tau: number): void {
    if (!this.overlayNoise && level <= 0) return;
    (this.overlayNoise ??= new OverlayNoise(this.ctx, this.master)).set(level, tau);
  }

  /** The speakers of the current room (tests, HUD): source id and gain target. */
  get speakerInfo(): { id: string; gain: number }[] {
    return this.speakers.info;
  }

  /**
   * Move the speakers to another room's sources (room switch; the music keeps playing; speakers.ts). Every change
   * glides, so the switch makes no click.
   */
  setSources(list: Manifest['audio']['sources'], period = 0): void {
    this.setWrap(period, sourcesOf(list));
    this.speakers.setSources(sourcesOf(list));
  }

  /**
   * An outdoor field repeats every `period` m (2026-11-01): the listener is placed at its copy nearest the sources'
   * centre (x and z wrapped into centre -/+ period / 2), so the sound comes from the copy of the speakers nearest
   * the listener, the one drawn. Where that copy changes (period / 2 from the centre) the listener jumps a period:
   * a jump over TELEPORT_M, so the speakers dip as for a teleport. 0 = no wrapping (rooms).
   */
  private setWrap(period: number, sources: readonly { position: readonly number[] }[]): void {
    this.wrapP = period > 0 ? period : 0;
    const n = sources.length || 1;
    this.wrapC = [sources.reduce((a, s) => a + s.position[0]!, 0) / n, sources.reduce((a, s) => a + s.position[2]!, 0) / n];
  }

  /** Must be called from a user gesture the first time. */
  async resume(): Promise<void> {
    if (this.ctx.state !== 'running') await this.ctx.resume();
  }

  /**
   * Move the listener to the camera. At most ~30 updates per second; each AudioParam keeps one pending
   * automation event (cancel + setTargetAtTime). Piling up an event per param per frame made WebKit's audio
   * thread stall (Safari stutter).
   */
  updateListener(camera: Camera, force = false): void {
    const now = performance.now();
    if (!force && now - this.lastListener < 33) { this.listenerPending = camera; return; }
    this.lastListener = now;
    this.listenerPending = null;
    const l = this.ctx.listener;
    camera.getWorldPosition(_pos);
    if (this.wrapP) {
      _pos.x = this.wrapC[0] + wrapDelta(_pos.x - this.wrapC[0], this.wrapP);
      _pos.z = this.wrapC[1] + wrapDelta(_pos.z - this.wrapC[1], this.wrapP);
    }
    camera.getWorldDirection(_fwd);
    _up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    if (l.positionX) {
      const t = this.ctx.currentTime;
      // a room switch or a teleport: the speakers dip and the listener jumps at the dip's bottom (speakers.ts)
      let at = this.speakers.jumpPending;
      if (at === null && this.lastPos && _pos.distanceTo(this.lastPos) > TELEPORT_M) at = this.speakers.dip();
      const go = at === null ? (p: AudioParam, v: number) => follow(p, v, t) : (p: AudioParam, v: number) => jump(p, v, t, at!);
      go(l.positionX, _pos.x); go(l.positionY, _pos.y); go(l.positionZ, _pos.z);
      go(l.forwardX, _fwd.x); go(l.forwardY, _fwd.y); go(l.forwardZ, _fwd.z);
      go(l.upX, _up.x); go(l.upY, _up.y); go(l.upZ, _up.z);
    } else {
      // old Safari / Firefox
      const ll = l as unknown as { setPosition(x: number, y: number, z: number): void; setOrientation(a: number, b: number, c: number, d: number, e: number, f: number): void };
      ll.setPosition(_pos.x, _pos.y, _pos.z);
      ll.setOrientation(_fwd.x, _fwd.y, _fwd.z, _up.x, _up.y, _up.z);
    }
    this.speakers.listen([_pos.x, _pos.y, _pos.z]);
    (this.lastPos ??= new Vector3()).copy(_pos);
  }

  /** Apply a listener update that was skipped by the rate limit (call once per frame). */
  flushListener(): void {
    if (this.listenerPending && performance.now() - this.lastListener >= 33) this.updateListener(this.listenerPending, true);
  }

  /**
   * Load an item. If something is playing it fades out (the item's transition.fade_out_s, Voyage: 2 s) while the
   * new item loads, and the brown noise bridge comes up. Resolves when the new item's media metadata is known
   * (durations, so seeks can be computed). Does not start playback.
   */
  load(item: MediaItem): Promise<void> {
    const p = this.doLoad(item);
    this.loading = p;
    return p;
  }

  /** Resolves when the most recent load() has finished. */
  get ready(): Promise<void> {
    return this.loading ?? Promise.resolve();
  }

  private async doLoad(item: MediaItem): Promise<void> {
    const old = this.track;
    const oldItem = this.item;
    const wasPlaying = this.playing;
    this.track = null;
    this.item = item;
    this.playing = false;
    if (old) {
      if (wasPlaying) this.fadeOutAndStop(old, transitionOf(oldItem).fade_out_s);
      else this.stopTrack(old);
    }
    const tr = transitionOf(item);
    if (wasPlaying && tr.bridge_gain > 0) this.bridgeIn(tr.bridge_gain);
    this.onChange();

    const gain = this.ctx.createGain();
    gain.gain.value = FLOOR;
    gain.connect(this.mix);
    let t: Track;
    if (item.audio.length === 1 && item.audio[0]!.startsWith('synth:')) {
      this.synthCache ??= import('./synth').then(({ renderChordLoop }) => renderChordLoop(this.ctx.sampleRate)); // local test signal only
      const buffer = await this.synthCache;
      t = { kind: 'buffer', buffer, node: null, startedAt: 0, offset: 0, gain };
    } else {
      const els: HTMLAudioElement[] = [];
      const nodes: MediaElementAudioSourceNode[] = [];
      item.audio.forEach((url, i) => {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        // the first track is the clock and ends the item; the others (Voyage's ambient) loop under it
        el.loop = i > 0;
        el.src = url;
        const node = this.ctx.createMediaElementSource(el);
        node.connect(gain);
        nodes.push(node);
        els.push(el);
      });
      t = { kind: 'element', els, nodes, gain };
      els[0]!.addEventListener('ended', () => {
        if (this.track === t && this.playing) this.onEnded(item);
      });
      await Promise.all(els.map(waitMetadata));
    }
    if (this.item !== item) {
      this.stopTrack(t); // replaced while loading
      return;
    }
    this.track = t;
    this.onChange();
  }

  /** The current item's audio elements (tests, HUD). */
  get elements(): HTMLAudioElement[] {
    return this.track?.kind === 'element' ? this.track.els : [];
  }

  get duration(): number {
    const t = this.track;
    if (!t) return 0;
    if (t.kind === 'buffer') return t.buffer.duration;
    const d = t.els[0]!.duration;
    return Number.isFinite(d) ? d : this.item?.duration_s ?? 0;
  }

  /** Current playback position in seconds (the first track's clock). */
  get position(): number {
    const t = this.track;
    if (!t) return 0;
    if (t.kind === 'buffer') {
      const p = t.node ? t.offset + (this.ctx.currentTime - t.startedAt) : t.offset;
      return p % t.buffer.duration;
    }
    return t.els[0]!.currentTime;
  }

  /** Start (or resume) the current item: bridge noise up, tracks start at -60 dB and rise to 0 dB. */
  async play(at?: number): Promise<void> {
    const t = this.track;
    if (!t) return;
    await this.resume();
    if (at !== undefined) this.seek(at);
    if (this.playing || this.track !== t) return;
    const tr = transitionOf(this.item);
    if (tr.bridge_gain > 0) this.bridgeIn(tr.bridge_gain);
    const g = t.gain.gain;
    g.cancelScheduledValues(this.ctx.currentTime);
    g.setValueAtTime(FLOOR, this.ctx.currentTime);
    this.playing = true;
    if (t.kind === 'buffer') {
      if (!t.node) this.startBuffer(t, t.offset);
    } else {
      await Promise.all(t.els.map((e) => e.play().catch((err) => console.warn('[audio] play failed', e.src, err))));
      if (this.track !== t || !this.playing) return; // paused or replaced while starting
    }
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(FLOOR, now);
    g.exponentialRampToValueAtTime(1, now + Math.max(0.05, tr.fade_in_s));
    if (tr.bridge_gain > 0) this.bridgeOut(now + 0.4, tr.bridge_out_s);
    this.onChange();
  }

  /**
   * Pause. The music fades to -60 dB over PAUSE_FADE_S before it stops (2026-10-28: before, the gain dropped in one
   * sample, a click), and so do tracks still fading out from an item change.
   */
  pause(): void {
    const now = this.ctx.currentTime;
    for (const f of this.fading) {
      fadeTo(f.gain.gain, FLOOR, now, PAUSE_FADE_S);
      setTimeout(() => this.stopTrack(f), PAUSE_FADE_S * 1000 + 30);
    }
    this.fading.clear();
    this.bridgeOut(now, transitionOf(this.item).bridge_out_s);
    const t = this.track;
    if (!t) { this.playing = false; return; }
    if (t.kind === 'buffer') {
      if (t.node) {
        t.offset = this.position;
        const node = t.node;
        node.onended = () => node.disconnect();
        node.stop(now + PAUSE_FADE_S);
        t.node = null;
      }
    } else {
      // the elements stop after the fade, unless play() or another item came first
      setTimeout(() => { if (!this.playing && this.track === t) for (const e of t.els) e.pause(); }, PAUSE_FADE_S * 1000 + 20);
    }
    fadeTo(t.gain.gain, FLOOR, now, PAUSE_FADE_S);
    this.playing = false;
    this.onChange();
  }

  seek(s: number): void {
    const t = this.track;
    if (!t) return;
    this.counters.seeks++;
    const d = this.duration;
    const pos = d > 0 ? wrap(s, d) : Math.max(0, s);
    if (t.kind === 'buffer') {
      if (t.node) {
        t.node.onended = null;
        t.node.stop();
        t.node.disconnect();
        t.node = null;
        this.startBuffer(t, pos);
      } else {
        t.offset = pos;
      }
    } else {
      const lead = this.playing ? this.seekLead : 0;
      const p0 = wrap(pos + lead, d);
      t.els.forEach((e, i) => { e.currentTime = i === 0 ? p0 : wrap(p0, e.duration); });
      this.seekCheck = this.playing ? { target: pos, at: this.ctx.currentTime } : null;
    }
  }

  /**
   * Start the current item again from 0 (Next on a one-item list, or its end). The first track has stopped at its
   * end (`ended` pauses the element), so it is started again; seek alone left it silent while `playing` stayed true.
   */
  restart(): void {
    this.seek(0);
    const t = this.track;
    if (this.playing && t?.kind === 'element') {
      for (const e of t.els) if (e.paused) e.play().catch((err) => console.warn('[audio] play failed', e.src, err));
    }
  }

  /** Learn the seek lead from where the element is ~1.2 s after a seek (called from correctDrift). */
  private checkSeek(): void {
    const c = this.seekCheck, t = this.track;
    if (!c || !t || t.kind !== 'element' || !this.playing) { this.seekCheck = null; return; }
    const elapsed = this.ctx.currentTime - c.at;
    if (elapsed < 1.2) return;
    this.seekCheck = null;
    const residual = centred(c.target + elapsed - t.els[0]!.currentTime, this.duration);
    if (Math.abs(residual) > 1.5) return; // something else happened (stall, item change)
    this.seekLead = Math.min(1, Math.max(0, this.seekLead + residual * 0.8));
  }

  /**
   * Keep the other element tracks (Voyage's ambient loop) at the first track's clock modulo their own length,
   * so every listener hears the same ambient phase. Call ~1 Hz.
   */
  correctDrift(): void {
    this.checkSeek();
    const t = this.track;
    if (!t || t.kind !== 'element' || t.els.length < 2 || !this.playing) return;
    const ref = t.els[0]!.currentTime;
    for (let i = 1; i < t.els.length; i++) {
      const e = t.els[i]!;
      const want = wrap(ref, e.duration);
      const diff = centred(e.currentTime - want, e.duration);
      // two checks in a row over 0.12 s: Safari reports currentTime coarsely, and every seek is audible
      if (Math.abs(diff) > 0.12) {
        if (++this.ambientStrikes >= 2 || Math.abs(diff) > 0.5) { e.currentTime = want; this.counters.ambientResyncs++; this.ambientStrikes = 0; }
      } else this.ambientStrikes = 0;
    }
  }

  private startBuffer(t: Extract<Track, { kind: 'buffer' }>, offset: number): void {
    const n = this.ctx.createBufferSource();
    n.buffer = t.buffer;
    n.loop = true;
    n.connect(t.gain);
    t.offset = offset;
    t.startedAt = this.ctx.currentTime;
    n.start(0, offset);
    t.node = n;
  }

  private fadeOutAndStop(t: Track, seconds: number): void {
    const g = t.gain.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(FLOOR, g.value), now);
    g.exponentialRampToValueAtTime(FLOOR, now + Math.max(0.05, seconds));
    this.fading.add(t);
    setTimeout(() => {
      if (this.fading.delete(t)) this.stopTrack(t);
    }, seconds * 1000 + 150);
  }

  private bridgeIn(level: number): void {
    if (!this.noise) {
      this.noise = this.ctx.createBufferSource();
      this.noise.buffer = makeBrownNoise(this.ctx);
      this.noise.loop = true;
      this.noise.connect(this.noiseGain);
      this.noise.start();
    }
    const g = this.noiseGain.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(level, now + 0.5);
  }

  private bridgeOut(at: number, seconds: number): void {
    const g = this.noiseGain.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    if (at > now) g.linearRampToValueAtTime(Math.max(g.value, 0), at);
    g.linearRampToValueAtTime(0, Math.max(at, now) + Math.max(0.05, seconds));
  }

  private stopTrack(t: Track): void {
    if (t.kind === 'buffer') {
      if (t.node) { t.node.onended = null; t.node.stop(); t.node.disconnect(); t.node = null; }
    } else {
      for (const e of t.els) { e.pause(); e.removeAttribute('src'); e.load(); }
      for (const n of t.nodes) n.disconnect();
    }
    t.gain.disconnect();
  }
}

/** -60 dB: where Voyage starts and ends its fades. */
const FLOOR = 0.001;
/** Fade before a pause stops the music (s). */
const PAUSE_FADE_S = 0.04;

/** An exponential fade from the current value to v (> 0) over `s` seconds. */
function fadeTo(p: AudioParam, v: number, t: number, s: number): void {
  p.cancelScheduledValues(t);
  p.setValueAtTime(Math.max(FLOOR, p.value), t);
  p.exponentialRampToValueAtTime(v, t + s);
}

type Source = Manifest['audio']['sources'][number];

/** A scene without sound sources: one speaker in the middle of the listening room. */
function sourcesOf(list: Manifest['audio']['sources']): Source[] {
  return list.length ? list : [{ id: 'centre', position: [3.5, 1.2, -5], forward: [0, 0, 1] }];
}

function transitionOf(item: MediaItem | null): Transition {
  return item?.transition ?? DEFAULT_TRANSITION;
}

/** s wrapped into 0..d (a position on a loop of length d); unchanged without a usable d. */
function wrap(s: number, d: number): number {
  return Number.isFinite(d) && d > 0 ? ((s % d) + d) % d : s;
}

/** A difference of two positions on a loop of length d, wrapped into -d/2..d/2. */
function centred(x: number, d: number): number {
  return Number.isFinite(d) && d > 0 ? ((x % d) + d * 1.5) % d - d / 2 : x;
}

function waitMetadata(el: HTMLMediaElement): Promise<void> {
  if (el.readyState >= 1) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); el.removeEventListener('loadedmetadata', done); el.removeEventListener('error', fail); resolve(); };
    const fail = () => { console.warn('[audio] could not load', el.src, el.error?.message ?? ''); done(); };
    const timer = setTimeout(() => { console.warn('[audio] metadata timeout', el.src); done(); }, 15000);
    el.addEventListener('loadedmetadata', done);
    el.addEventListener('error', fail);
  });
}

/** A listener move longer than this (m) between two updates is a teleport: the speakers dip (speakers.ts). */
const TELEPORT_M = 1.0;

/** Set an AudioParam to v at `at` (the bottom of the speakers' dip), keeping it where it is until then. */
function jump(p: AudioParam, v: number, t: number, at: number): void {
  hold(p, t);
  p.setValueAtTime(v, at);
}

/** Glide an AudioParam to v with one pending event (no build-up of automation events). */
function follow(p: AudioParam, v: number, t: number): void {
  // cancelAndHoldAtTime ends the previous glide where it is (Chrome, Safari); Firefox lacks it and prunes
  // past events itself
  const h = (p as AudioParam & { cancelAndHoldAtTime?: (t: number) => AudioParam }).cancelAndHoldAtTime;
  if (h) h.call(p, t);
  p.setTargetAtTime(v, t, 0.015);
}
